import { useEffect, useState, useRef, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { HelmetProvider } from "react-helmet-async";
import SetUsernameDialog from "@/components/SetUsernameDialog";

// Eager-loaded pages (needed immediately or for auth flow)
import AuthCallback from "@/pages/AuthCallback";

// Core pages: direct import for instant load on return
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import DashboardPage from "@/pages/DashboardPage";
import EventsPage from "@/pages/EventsPage";
import EventDetailsPage from "@/pages/EventDetailsPage";
import DigestLandingPage from "@/pages/DigestLandingPage";
import MyProfilePage from "@/pages/MyProfilePage";
import CreateEventPage from "@/pages/CreateEventPage";
import CreateEventAutoPage from "@/pages/CreateEventAutoPage";
import AdminPendingEventsPage from "@/pages/AdminPendingEventsPage";
import CivilianNightCreatePage from "@/pages/CivilianNightCreatePage";
import CivilianNightDetailPage from "@/pages/CivilianNightDetailPage";
import CivilianNightsListPage from "@/pages/CivilianNightsListPage";
import AdminEventModerationPage from "@/pages/AdminEventModerationPage";
import AdminIGPostsPage from "@/pages/AdminIGPostsPage";
import AdminScrapeDiagnosePage from "@/pages/AdminScrapeDiagnosePage";
import AdminScrapeHistoryPage from "@/pages/AdminScrapeHistoryPage";
import AdminAutoScrapeSettingsPage from "@/pages/AdminAutoScrapeSettingsPage";
import TekkiPage from "@/pages/TekkiPage";
import MyTicketsPage from "@/pages/MyTicketsPage";
import CrisisZonePage from "@/pages/CrisisZonePage";
import BlackoutGate from "@/components/blackout/BlackoutGate";
import BlackoutUnlockedPage from "@/pages/BlackoutUnlockedPage";

// Secondary pages: lazy loaded
const VerifyEmailPage = lazy(() => import("@/pages/VerifyEmailPage"));
const SportsPreviewPage = lazy(() => import("@/pages/SportsPreviewPage"));
const ForgotPasswordPage = lazy(() => import("@/pages/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("@/pages/ResetPasswordPage"));
const BookingPage = lazy(() => import("@/pages/BookingPage"));
const BookingAlertPage = lazy(() => import("@/pages/BookingAlertPage"));
const CreateTripPage = lazy(() => import("@/pages/CreateTripPage"));
const AdminPage = lazy(() => import("@/pages/AdminPage"));
const AdminLicensePage = lazy(() => import("@/pages/AdminLicensePage"));
const AdminPanelPage = lazy(() => import("@/pages/AdminPanelPage"));
const AdminManageTripsPage = lazy(() => import("@/pages/AdminManageTripsPage"));
const AdminEmailDuplicatesPage = lazy(() => import("@/pages/AdminEmailDuplicatesPage"));
const AdminEmailDigestPage = lazy(() => import("@/pages/AdminEmailDigestPage"));
const ReportPage = lazy(() => import("@/pages/ReportPage"));
const SafetyPage = lazy(() => import("@/pages/SafetyPage"));
const FuelEstimatorPage = lazy(() => import("@/pages/FuelEstimatorPage"));
const CalendarPage = lazy(() => import("@/pages/CalendarPage"));
const RequestRidePage = lazy(() => import("@/pages/RequestRidePage"));
const OpenRequestsPage = lazy(() => import("@/pages/OpenRequestsPage"));
const MessagesPage = lazy(() => import("@/pages/MessagesPage"));
const ChatPage = lazy(() => import("@/pages/ChatPage"));
const NotificationsPage = lazy(() => import("@/pages/NotificationsPage"));
const UserProfilePage = lazy(() => import("@/pages/UserProfilePage"));
const SupportPage = lazy(() => import("@/pages/SupportPage"));
const TermsPage = lazy(() => import("@/pages/TermsPage"));
const PrivacyPage = lazy(() => import("@/pages/PrivacyPage"));
const TripHistoryPage = lazy(() => import("@/pages/TripHistoryPage"));
const SiteSettingsPage = lazy(() => import("@/pages/SiteSettingsPage"));
const RefundPolicyPage = lazy(() => import("@/pages/RefundPolicyPage"));
const ContactPage = lazy(() => import("@/pages/ContactPage"));
const VendorSpacePage = lazy(() => import("@/pages/VendorSpacePage"));
const VendorEditorPage = lazy(() => import("@/pages/VendorEditorPage"));
const SharedTripPage = lazy(() => import("@/pages/SharedTripPage"));
const TicketConfirmPage = lazy(() => import("@/pages/TicketConfirmPage"));
const CheckInPage = lazy(() => import("@/pages/CheckInPage"));
const TicketViewPage = lazy(() => import("@/pages/TicketViewPage"));
const OrderSuccessPage = lazy(() => import("@/pages/OrderSuccessPage"));
const RollFlixPage = lazy(() => import("@/pages/RollFlixPage"));
const SetupCornerPage = lazy(() => import("@/pages/SetupCornerPage"));
const SellerOrdersPage = lazy(() => import("@/pages/SellerOrdersPage"));
const SandboxCheckoutPage = lazy(() => import("@/pages/SandboxCheckoutPage"));
const QuestionnairePage = lazy(() => import("@/pages/QuestionnairePage"));
const FeedbackResultsPage = lazy(() => import("@/pages/FeedbackResultsPage"));
const SafeSpacePage = lazy(() => import("@/pages/SafeSpacePage"));
const Sk8ShopPage = lazy(() => import("@/pages/Sk8ShopPage"));
const MarketplaceDesignPreviewPage = lazy(() => import("@/pages/MarketplaceDesignPreviewPage"));
const HireHubPage = lazy(() => import("@/pages/HireHubPage"));
const HireListingDetailPage = lazy(() => import("@/pages/HireListingDetailPage"));
const HireBookingsPage = lazy(() => import("@/pages/HireBookingsPage"));
const HireOrderDetailPage = lazy(() => import("@/pages/HireOrderDetailPage"));
const MyBookingsPage = lazy(() => import("@/pages/MyBookingsPage"));
const InstagramBannerPage = lazy(() => import("@/pages/InstagramBannerPage"));
const RantingSpotPage = lazy(() => import("@/pages/RantingSpotPage"));
const FloorTaxPage = lazy(() => import("@/pages/FloorTaxPage"));
const PayoutDashboardPage = lazy(() => import("@/pages/PayoutDashboardPage"));
const WorldTripsPage = lazy(() => import("@/pages/WorldTripsPage"));
const ShowcasePage = lazy(() => import("@/pages/ShowcasePage"));
const FAQPage = lazy(() => import("@/pages/FAQPage"));
const MenuLabPage = lazy(() => import("@/pages/MenuLabPage"));

// Context
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { MusicPlayerProvider } from "@/context/MusicPlayerContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { GeoProvider } from "@/context/GeoContext";
import { ChatProvider } from "@/context/ChatContext";
import { FavouritesProvider } from "@/context/FavouritesContext";
import { NearbyProvider } from "@/context/NearbyContext";

// Components
import InstallPWA from "@/components/InstallPWA";
import GlobalMusicPlayer from "@/components/GlobalMusicPlayer";
import GlobalEventChat from "@/components/EventGroupChat";
import ScrollToTop from "@/components/ScrollToTop";
import ErrorBoundary from "@/components/ErrorBoundary";
import GuidedTourWrapper from "@/components/GuidedTourWrapper";
import WatermarkBackground from "@/components/WatermarkBackground";
import useHardwareBack from "@/hooks/useHardwareBack";

// Error tracking
import { installGlobalErrorTracking, installAxiosInterceptor } from "@/utils/errorTracker";
import axios from "axios";

// Install global listeners once on load
installGlobalErrorTracking();
installAxiosInterceptor(axios);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Register Service Worker
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js')
      .then((reg) => console.log('SW registered:', reg.scope))
      .catch((err) => console.log('SW failed:', err));
  });
}
// Protected Route wrapper
const ProtectedRoute = ({ children, allowedRoles = [] }) => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Check if we have a token in localStorage (even if user state is not yet loaded)
  const hasToken = localStorage.getItem('rollm8s_token') || localStorage.getItem('token');

  useEffect(() => {
    // Only redirect if we're done loading AND there's no user AND no token
    if (!loading && !user && !hasToken) {
      navigate("/login", { state: { from: location } });
    }
    // Check roles - also allow if user.is_admin is true for admin routes
    if (!loading && user && allowedRoles.length > 0) {
      const hasRole = allowedRoles.includes(user.role);
      const isAdminRoute = allowedRoles.includes('admin');
      const userIsAdmin = user.is_admin === true;
      
      if (!hasRole && !(isAdminRoute && userIsAdmin)) {
        navigate("/dashboard");
      }
    }
  }, [user, loading, navigate, location, allowedRoles, hasToken]);

  // Show loading while auth is being checked
  if (loading || (!user && hasToken)) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse-glow w-16 h-16 rounded-full bg-primary/20" />
      </div>
    );
  }

  return user ? children : null;
};

// App Router with session_id detection
function AppRouter() {
  const location = useLocation();
  // Wires Android hardware back → in-app navigation + double-press-to-exit.
  // No-op on web/PWA.
  useHardwareBack();

  // CRITICAL: Check URL fragment synchronously for session_id (Google OAuth callback)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Suspense fallback={<div className="min-h-screen bg-background" />}>
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/_dev/sports" element={<SportsPreviewPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/events" element={<EventsPage />} />
      <Route path="/d/:digestId" element={<DigestLandingPage />} />
      <Route path="/events/:eventId" element={<EventDetailsPage />} />
      <Route path="/safety" element={<SafetyPage />} />
      <Route path="/ride/:tripId" element={<SharedTripPage />} />
      <Route path="/tickets/confirm" element={<TicketConfirmPage />} />
      <Route path="/tickets/view/:ticketId" element={<TicketViewPage />} />
      <Route path="/order-success" element={<OrderSuccessPage />} />
      <Route path="/my-tickets" element={<MyTicketsPage />} />
      <Route path="/events/:eventId/check-in" element={<CheckInPage />} />
      <Route path="/fuel-estimator" element={<FuelEstimatorPage />} />
      <Route path="/calendar" element={<CalendarPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/tekki" element={<TekkiPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/instagram-banner" element={<InstagramBannerPage />} />
      <Route path="/ranting-spot" element={<RantingSpotPage />} />
      <Route path="/floor-tax" element={<FloorTaxPage />} />
      <Route path="/world-trips" element={<WorldTripsPage />} />
      <Route path="/showcase" element={<ShowcasePage />} />
      <Route path="/faq" element={<FAQPage />} />
      <Route path="/menu-lab" element={<MenuLabPage />} />
      <Route path="/rollflix" element={<RollFlixPage />} />
      {/* Legacy URL redirect — keeps old bookmarks working without the react-router warning */}
      <Route path="/community" element={<Navigate to="/rollflix" replace />} />
      <Route path="/setup-corner" element={<SetupCornerPage />} />
      <Route
        path="/trip-history"
        element={
          <ProtectedRoute>
            <TripHistoryPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/support"
        element={
          <ProtectedRoute>
            <SupportPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/booking/:bookingId"
        element={
          <ProtectedRoute>
            <BookingPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/booking-alert/:bookingId"
        element={
          <ProtectedRoute>
            <BookingAlertPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/create-trip"
        element={
          <ProtectedRoute>
            <CreateTripPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/create-event"
        element={
          <ProtectedRoute>
            <CreateEventPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/create-event/auto"
        element={
          <ProtectedRoute>
            <CreateEventAutoPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <AdminPanelPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/manage-trips"
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <AdminManageTripsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/licenses"
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <AdminLicensePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/panel"
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <AdminPanelPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/email-duplicates"
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <AdminEmailDuplicatesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/email-digest"
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <AdminEmailDigestPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/pending-events"
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <AdminPendingEventsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/event-moderation"
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <AdminEventModerationPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/ig-posts"
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <AdminIGPostsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/scrape-diagnose"
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <AdminScrapeDiagnosePage />
          </ProtectedRoute>
        }
      />
      {/* Civilian Nights Out — link-only social meetups. Create + view are
          public; the list view requires login. */}
      <Route path="/civilian/new" element={<CivilianNightCreatePage />} />
      <Route path="/civilian/:civId" element={<CivilianNightDetailPage />} />
      <Route path="/civilian" element={<CivilianNightsListPage />} />
      <Route
        path="/admin/scrape-history"
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <AdminScrapeHistoryPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/scrape-settings"
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <AdminAutoScrapeSettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/report"
        element={
          <ProtectedRoute>
            <ReportPage />
          </ProtectedRoute>
        }
      />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route
        path="/request-ride"
        element={
          <ProtectedRoute>
            <RequestRidePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/open-requests"
        element={
          <ProtectedRoute>
            <OpenRequestsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/messages"
        element={
          <ProtectedRoute>
            <MessagesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/chat/:conversationId"
        element={
          <ProtectedRoute>
            <ChatPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/notifications"
        element={
          <ProtectedRoute>
            <NotificationsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile/:userId"
        element={
          <ProtectedRoute>
            <UserProfilePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-profile"
        element={<MyProfilePage />}
      />
      <Route
        path="/profile"
        element={<MyProfilePage />}
      />
      <Route
        path="/site-settings"
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <SiteSettingsPage />
          </ProtectedRoute>
        }
      />
      <Route path="/refund-policy" element={<RefundPolicyPage />} />
      <Route path="/contact" element={<ContactPage />} />
      {/* Vendor Spaces */}
      <Route path="/space/:slug" element={<VendorSpacePage />} />
      <Route
        path="/my-page"
        element={
          <ProtectedRoute>
            <VendorEditorPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-space"
        element={
          <ProtectedRoute>
            <VendorEditorPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/corner/:spaceId"
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <VendorEditorPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-sales"
        element={
          <ProtectedRoute>
            <SellerOrdersPage />
          </ProtectedRoute>
        }
      />
      {/* Sandbox Checkout */}
      <Route path="/sandbox-checkout/:sandboxId" element={<SandboxCheckoutPage />} />
      {/* Beta Test Feedback */}
      <Route path="/beta-test" element={<QuestionnairePage />} />
      <Route path="/questionnaire" element={<QuestionnairePage />} />
      <Route
        path="/safe-space"
        element={
          <ProtectedRoute>
            <SafeSpacePage />
          </ProtectedRoute>
        }
      />
      <Route path="/sk8-shop" element={<Sk8ShopPage />} />
      <Route path="/marketplace-design-preview" element={<MarketplaceDesignPreviewPage />} />
      <Route path="/hire" element={<HireHubPage />} />
      <Route path="/hire/bookings" element={<HireBookingsPage />} />
      <Route path="/hire/order/:orderId" element={<HireOrderDetailPage />} />
      <Route path="/my-bookings" element={<MyBookingsPage />} />
      <Route path="/hire/:listingId" element={<HireListingDetailPage />} />
      <Route path="/blackout" element={<BlackoutGate />} />
      <Route path="/blackout/unlocked" element={<BlackoutUnlockedPage />} />
      <Route path="/blackout-zone" element={<Navigate to="/blackout" replace />} />
      <Route path="/crisis-zone" element={<Navigate to="/blackout" replace />} />
      <Route
        path="/payouts"
        element={
          <ProtectedRoute>
            <PayoutDashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/feedback"
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <FeedbackResultsPage />
          </ProtectedRoute>
        }
      />
    </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <HelmetProvider>
      <div className="App min-h-screen bg-background md:pb-0">
        <BrowserRouter>
          <ScrollToTop />
          <ThemeProvider>
            <WatermarkBackground />
            <AuthProvider>
              <GeoProvider>
              <MusicPlayerProvider>
                <ChatProvider>
                  <FavouritesProvider>
                  <NearbyProvider>
                  <AppRouter />
                  {/* <GuidedTourWrapper /> */}
                  <GlobalEventChat />
                  <GlobalMusicPlayer />
                  <SetUsernameDialog />
                  {/* InstallPWA hidden */}
                  <Toaster position="top-right" richColors />
                  </NearbyProvider>
                  </FavouritesProvider>
                </ChatProvider>
              </MusicPlayerProvider>
              </GeoProvider>
            </AuthProvider>
          </ThemeProvider>
        </BrowserRouter>
      </div>
      </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;
